import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  PageWrap, Header, PeriodoBar, BarraBusqueda, EstadoCarga, AtencionBanner,
  ComparaBanner, KpiCard, MiniDonut, SemaforoBadge, ModalShell, Tendencia, cs,
  EstadoBadge, SemaforoResumen, ORDEN_ESTADO, SerieTemporal,
  semaforo, C, BG, calcPeriodo, prevPeriodo, todayISO, lunesISO,
} from "../analisis/comun";

// Banderas por nombre de país (este endpoint devuelve nombre completo)
const BANDERA = { 'España': '🇪🇸', 'Francia': '🇫🇷', 'Italia': '🇮🇹', 'Portugal': '🇵🇹', 'Alemania': '🇩🇪', 'China': '🇨🇳', 'México': '🇲🇽', 'Colombia': '🇨🇴', 'Argentina': '🇦🇷', 'USA': '🇺🇸', 'Brasil': '🇧🇷' };

// ─── Cálculo de porcentajes (propios de este endpoint) ────────────────────────
function pctLista(lista) {
  if (!lista || lista.length === 0) return null;
  let s = 0, m = 0;
  lista.forEach(q => { s += q.stats.verde * 2 + (q.stats.amarillo || 0); m += (q.stats.verde + (q.stats.amarillo || 0) + (q.stats.rojo || 0)) * 2; });
  return m === 0 ? null : Math.round((s / m) * 100);
}
const resPct = (al) => pctLista(al.preguntas.filter(q => q.tipo !== 'si_no' && q.stats.total > 0));
const esAlertaRes = (al) => { const p = resPct(al); return p !== null && p < 60; };
const noVistoRes = (al) => { const v = al.preguntas.find(q => q.tipo === 'si_no'); return v && v.stats.si === 0 && v.stats.no > 0; };

// Estado del piso según reportes hechos + resultados (🔴/🟡/🟢/⬜)
const estadoPisoRep = (p) => {
  if (!p.n_reportes) return 'sin_reportes';
  const alerts = (p.residentes_list || []).filter(esAlertaRes).length;
  const st = semaforo(pctLista(p.piso_scores));
  if (st === 'rojo' || alerts > 0) return 'critico';
  const noV = (p.residentes_list || []).filter(noVistoRes).length;
  if (st === 'amarillo' || noV > 0) return 'atencion';
  return 'bien';
};

// ─── Tab Estado del piso ──────────────────────────────────────────────────────
const TabPiso = ({ p }) => {
  const hayDatos = p.piso_scores.some(q => q.stats.total > 0);
  if (!hayDatos) return (<div style={{ textAlign: 'center', padding: '40px 20px', color: C.gris }}><div style={{ fontSize: 48, marginBottom: 10 }}>📋</div><div style={{ fontSize: 16, fontWeight: 700, color: '#374151' }}>Sin reportes de piso</div></div>);
  const pct = pctLista(p.piso_scores);
  return (<div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <div style={{ ...cs.sec, display: 'flex', alignItems: 'center', gap: 14 }}><MiniDonut pct={pct} size={64} /><div><div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>Estado general del piso</div><SemaforoBadge tipo={semaforo(pct)} label={pct != null ? `${pct}%` : '—'} /><div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{p.n_reportes} reportes en el período</div></div></div>
    <div style={cs.sec}><h4 style={cs.sh}>📊 Criterios del piso</h4>
      {p.piso_scores.filter(q => q.stats.total > 0).map(q => { const t = q.stats.verde + q.stats.amarillo + q.stats.rojo; const pV = t > 0 ? Math.round((q.stats.verde / t) * 100) : 0, pA = t > 0 ? Math.round((q.stats.amarillo / t) * 100) : 0, pR = 100 - pV - pA; return (<div key={q.id_pregunta} style={{ marginBottom: 12 }}><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}><span style={{ fontSize: 18 }}>{q.emoji}</span><span style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>{q.subcategoria}</span><span style={{ fontSize: 13, fontWeight: 800, color: C[semaforo(pV)] }}>{pV}% 🟢</span></div><div style={{ display: 'flex', height: 10, borderRadius: 99, overflow: 'hidden' }}>{pV > 0 && <div style={{ width: `${pV}%`, background: C.verde }} />}{pA > 0 && <div style={{ width: `${pA}%`, background: C.amarillo }} />}{pR > 0 && <div style={{ width: `${pR}%`, background: C.rojo }} />}</div><div style={{ display: 'flex', gap: 12, marginTop: 3 }}><span style={{ fontSize: 11, color: C.verde }}>🟢{q.stats.verde}</span><span style={{ fontSize: 11, color: C.amarillo }}>🟡{q.stats.amarillo}</span><span style={{ fontSize: 11, color: C.rojo }}>🔴{q.stats.rojo}</span></div></div>); })}
    </div>
  </div>);
};

// ─── Tab Residentes ───────────────────────────────────────────────────────────
const TabResidentes = ({ p }) => {
  const [expanded, setExpanded] = useState(null);
  if (!p.residentes_list || p.residentes_list.length === 0) return (<div style={{ textAlign: 'center', padding: '40px 20px', color: C.gris }}><div style={{ fontSize: 48, marginBottom: 10 }}>👥</div><div style={{ fontSize: 16, fontWeight: 700, color: '#374151' }}>Sin datos de residentes</div></div>);
  const ordenados = [...p.residentes_list].sort((a, b) => (esAlertaRes(a) === esAlertaRes(b) ? 0 : esAlertaRes(a) ? -1 : 1));
  return (<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    <div style={{ fontSize: 12, color: '#64748b' }}>🟢 bien · 🟡 regular · 🔴 mal · ⬜ desconoce (sin información)</div>
    {ordenados.map(al => {
      const pregVisto = al.preguntas.find(q => q.tipo === 'si_no');
      const otrasPreg = al.preguntas.filter(q => q.tipo !== 'si_no' && q.stats.total > 0);
      const pctOtras = pctLista(otrasPreg);
      const isExp = expanded === al.id_alumno;
      const bandera = BANDERA[al.pais] || '🌍';
      return (<div key={al.id_alumno} style={{ background: '#fff', borderRadius: 12, border: `1.5px solid ${isExp ? '#1e293b' : (esAlertaRes(al) ? '#fca5a5' : '#e2e8f0')}`, overflow: 'hidden' }}>
        <div onClick={() => setExpanded(isExp ? null : al.id_alumno)} style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <MiniDonut pct={pctOtras} size={44} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{bandera} {al.nombre}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>Hab. {al.habitacion || '—'} {al.pais ? `· ${al.pais}` : ''}</div>
          </div>
          {pregVisto && <span style={{ fontSize: 12, fontWeight: 700, color: pregVisto.stats.si > 0 ? C.verde : C.gris, background: pregVisto.stats.si > 0 ? BG.verde : BG.gris, borderRadius: 10, padding: '3px 8px' }}>{pregVisto.stats.si > 0 ? '👁️ Visto' : '👁️ No visto'}</span>}
          <span style={{ color: '#94a3b8', fontSize: 14 }}>{isExp ? '▲' : '▼'}</span>
        </div>
        {isExp && otrasPreg.length > 0 && <div style={{ padding: '0 14px 14px', borderTop: '1px solid #f1f5f9' }}>
          {otrasPreg.map(q => {
            const t = q.stats.verde + (q.stats.amarillo || 0) + q.stats.rojo + (q.stats.desconozco || 0);
            const pV = t > 0 ? Math.round(((q.stats.verde || 0) / t) * 100) : 0;
            const pA = t > 0 ? Math.round(((q.stats.amarillo || 0) / t) * 100) : 0;
            const pR = t > 0 ? Math.round(((q.stats.rojo || 0) / t) * 100) : 0;
            const pD = t > 0 ? Math.round(((q.stats.desconozco || 0) / t) * 100) : 0;
            return (<div key={q.id_pregunta} style={{ marginBottom: 10, paddingTop: 10, borderTop: '1px solid #f8fafc' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}><span style={{ fontSize: 16 }}>{q.emoji}</span><span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{q.subcategoria}</span></div>
              <div style={{ display: 'flex', height: 10, borderRadius: 99, overflow: 'hidden' }}>{pV > 0 && <div style={{ width: `${pV}%`, background: C.verde }} />}{pA > 0 && <div style={{ width: `${pA}%`, background: C.amarillo }} />}{pR > 0 && <div style={{ width: `${pR}%`, background: C.rojo }} />}{pD > 0 && <div style={{ width: `${pD}%`, background: C.gris }} />}</div>
              <div style={{ display: 'flex', gap: 10, marginTop: 3, flexWrap: 'wrap' }}>{q.stats.verde > 0 && <span style={{ fontSize: 11, color: C.verde }}>🟢{q.stats.verde}</span>}{(q.stats.amarillo || 0) > 0 && <span style={{ fontSize: 11, color: C.amarillo }}>🟡{q.stats.amarillo}</span>}{q.stats.rojo > 0 && <span style={{ fontSize: 11, color: C.rojo }}>🔴{q.stats.rojo}</span>}{(q.stats.desconozco || 0) > 0 && <span style={{ fontSize: 11, color: C.gris }}>⬜{q.stats.desconozco}</span>}</div>
            </div>);
          })}
        </div>}
      </div>);
    })}
  </div>);
};

// ─── Tab Evolución ────────────────────────────────────────────────────────────
const TabEvolucion = ({ p }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    <SerieTemporal
      data={p.serie_piso_scores || []}
      series={[{ key: 'piso', label: 'Estado piso %', color: '#3b82f6' }]}
      titulo="Evolución semanal del estado del piso"
      height={200}
    />
  </div>
);

// ─── Modal detalle piso ───────────────────────────────────────────────────────
const DetalleModal = ({ p, delta, onClose }) => {
  const [tab, setTab] = useState('piso');
  const st = semaforo(pctLista(p.piso_scores));
  const nRes = p.residentes_list?.length || 0;
  return (
    <ModalShell
      titulo={p.abreviatura}
      subtitulo={`Supervisora: ${p.supervisora} · Asistenta: ${p.asistenta || '—'} · ${p.n_reportes} reportes${delta != null ? '' : ''}`}
      colorBorde={C[st]}
      tabs={[{ key: 'piso', label: '🏠 Estado piso' }, { key: 'residentes', label: `👥 Residentes (${nRes})` }, { key: 'evolucion', label: '📊 Evolución' }]}
      tab={tab} setTab={setTab} onClose={onClose}
    >
      {tab === 'piso' && (<>
        {delta != null && <div style={{ marginBottom: 12, fontSize: 13, color: '#475569' }}>Estado <Tendencia delta={delta} /> <span style={{ color: '#94a3b8' }}>vs período anterior</span></div>}
        <TabPiso p={p} />
      </>)}
      {tab === 'residentes' && <TabResidentes p={p} />}
      {tab === 'evolucion' && <TabEvolucion p={p} />}
    </ModalShell>
  );
};

// ─── Tarjeta piso ─────────────────────────────────────────────────────────────
const TarjetaPiso = ({ p, delta, onClick }) => {
  const pctP = pctLista(p.piso_scores);
  const stP = semaforo(pctP);
  const nRes = p.residentes_list?.length || 0;
  const nAlerts = p.residentes_list?.filter(esAlertaRes).length || 0;
  const noVisto = p.residentes_list?.filter(noVistoRes).length || 0;
  const alert = stP === 'rojo' || nAlerts > 0;
  return (<div onClick={onClick} style={{ background: '#fff', borderRadius: 14, padding: '12px 14px', marginBottom: 8, cursor: 'pointer', border: `1.5px solid ${alert ? '#fca5a5' : '#e2e8f0'}`, borderLeftWidth: 5, borderLeftColor: C[stP], boxShadow: alert ? '0 2px 8px rgba(220,38,38,0.1)' : '0 1px 4px rgba(0,0,0,0.05)' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <MiniDonut pct={pctP} size={50} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
          {alert && <span>⚠️</span>}
          <span style={{ fontWeight: 800, fontSize: 15, color: '#1e293b' }}>{p.abreviatura}</span>
          <EstadoBadge estado={estadoPisoRep(p)} />
          <Tendencia delta={delta} />
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>{p.supervisora} · {p.asistenta || 'sin asistenta'}{p.ultima_fecha ? ` · últ. ${p.ultima_fecha}` : ''}</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <SemaforoBadge tipo={stP} label={`🏠 ${pctP != null ? pctP + '%' : '—'}`} />
          {nRes > 0 && <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', background: '#f1f5f9', borderRadius: 12, padding: '3px 8px' }}>👥 {nRes} residentes</span>}
          {nAlerts > 0 && <span style={{ fontSize: 12, fontWeight: 700, color: C.rojo, background: BG.rojo, borderRadius: 12, padding: '3px 8px' }}>⚠️ {nAlerts} alertas</span>}
          {noVisto > 0 && <span style={{ fontSize: 12, fontWeight: 700, color: C.amarillo, background: BG.amarillo, borderRadius: 12, padding: '3px 8px' }}>👁️ {noVisto} no vistos</span>}
          {p.n_reportes === 0 && <span style={{ fontSize: 12, color: C.gris }}>Sin reportes</span>}
        </div>
      </div>
      <span style={{ color: '#94a3b8', fontSize: 18, flexShrink: 0 }}>▶</span>
    </div>
  </div>);
};

// ─── Componente principal ─────────────────────────────────────────────────────
export default function AnalisisPisos() {
  const navigate = useNavigate();
  const [periodoKey, setPeriodoKey] = useState('mes');
  const [custom, setCustom] = useState({ desde: lunesISO(), hasta: todayISO() });
  const [datos, setDatos] = useState(null);
  const [datosPrev, setDatosPrev] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sel, setSel] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState('estado');
  const [filtroEstado, setFiltroEstado] = useState(null);

  const periodo = periodoKey === 'custom' ? custom : calcPeriodo(periodoKey);
  const { desde, hasta } = periodo;

  const fetchPeriod = useCallback(async (d, h) => {
    const res = await axios.get(`/api/analisis/pisos-reporte?desde=${d}&hasta=${h}`);
    return res.data;
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const pp = prevPeriodo({ desde, hasta });
      const [curr, prev] = await Promise.all([fetchPeriod(desde, hasta), fetchPeriod(pp.desde, pp.hasta)]);
      setDatos(curr); setDatosPrev(prev);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar.');
    } finally {
      setLoading(false);
    }
  }, [desde, hasta, fetchPeriod]);

  useEffect(() => { cargar(); }, [cargar]);

  const prevMap = useMemo(() => Object.fromEntries((datosPrev?.pisos || []).map(p => [p.id_piso, p])), [datosPrev]);
  const deltaDe = useCallback((p) => {
    const prev = prevMap[p.id_piso];
    const cur = pctLista(p.piso_scores), prv = prev ? pctLista(prev.piso_scores) : null;
    return cur != null && prv != null ? cur - prv : null;
  }, [prevMap]);

  const pisos = useMemo(() => {
    let l = datos?.pisos || [];
    if (busqueda.trim()) { const q = busqueda.toLowerCase(); l = l.filter(p => (p.abreviatura + ' ' + (p.supervisora || '')).toLowerCase().includes(q)); }
    if (filtroEstado) l = l.filter(p => estadoPisoRep(p) === filtroEstado);
    const s = [...l];
    if (orden === 'estado') s.sort((a, b) => ORDEN_ESTADO[estadoPisoRep(a)] - ORDEN_ESTADO[estadoPisoRep(b)]);
    else if (orden === 'nombre') s.sort((a, b) => (a.abreviatura || '').localeCompare(b.abreviatura || ''));
    else if (orden === 'residentes') s.sort((a, b) => b.residentes_list.length - a.residentes_list.length);
    else if (orden === 'reportes') s.sort((a, b) => b.n_reportes - a.n_reportes);
    return s;
  }, [datos, busqueda, orden, filtroEstado]);

  const selObj = useMemo(() => pisos.find(p => p.id_piso === sel), [pisos, sel]);

  // KPIs + atención
  const todos = datos?.pisos || [];
  const cnt = { verde: 0, amarillo: 0, rojo: 0, sinReporte: 0 };
  todos.forEach(p => { if (p.n_reportes === 0) { cnt.sinReporte++; return; } cnt[semaforo(pctLista(p.piso_scores))]++; });
  const estadoCounts = useMemo(() => {
    const c = { critico: 0, atencion: 0, bien: 0, sin_reportes: 0 };
    todos.forEach(p => { c[estadoPisoRep(p)]++; });
    return c;
  }, [todos]);
  const totalRes = todos.reduce((s, p) => s + (p.residentes_list?.length || 0), 0);
  const totalAlerts = todos.reduce((s, p) => s + (p.residentes_list?.filter(esAlertaRes).length || 0), 0);
  const totalNoVistos = todos.reduce((s, p) => s + (p.residentes_list?.filter(noVistoRes).length || 0), 0);

  const atencion = useMemo(() => {
    const items = [];
    if (cnt.rojo > 0) items.push({ icon: '🔴', severidad: 'rojo', n: cnt.rojo, texto: `${cnt.rojo} piso${cnt.rojo !== 1 ? 's' : ''} en estado crítico` });
    if (totalAlerts > 0) items.push({ icon: '⚠️', severidad: 'rojo', n: totalAlerts, texto: `${totalAlerts} residente${totalAlerts !== 1 ? 's' : ''} con valoración baja` });
    if (cnt.sinReporte > 0) items.push({ icon: '📋', severidad: 'amarillo', n: cnt.sinReporte, texto: `${cnt.sinReporte} piso${cnt.sinReporte !== 1 ? 's' : ''} sin reportes` });
    if (totalNoVistos > 0) items.push({ icon: '👁️', severidad: 'amarillo', n: totalNoVistos, texto: `${totalNoVistos} residente${totalNoVistos !== 1 ? 's' : ''} sin ver` });
    return items;
  }, [cnt.rojo, cnt.sinReporte, totalAlerts, totalNoVistos]);

  const pp = prevPeriodo({ desde, hasta });
  const vacio = !loading && !error && pisos.length === 0;

  return (
    <PageWrap>
      <Header icon="🏠" title="Estado de Pisos" count={pisos.length} countLabel="pisos" onBack={() => navigate(-1)} />
      <PeriodoBar periodoKey={periodoKey} setPeriodoKey={setPeriodoKey} custom={custom} setCustom={setCustom} />
      {!loading && !error && <ComparaBanner prev={pp} />}

      <EstadoCarga loading={loading} error={error} onRetry={cargar} />

      {!loading && !error && datos && (
        <>
          <AtencionBanner items={atencion} okTexto="Todos los pisos en buen estado, sin alertas." />

          <SemaforoResumen counts={estadoCounts} filtro={filtroEstado} setFiltro={setFiltroEstado} />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, padding: '4px 14px 0' }}>
            <KpiCard icon="🟢" label="Pisos OK" value={cnt.verde} bg="#dcfce7" color="#16a34a" />
            <KpiCard icon="🔴" label="Críticos" value={cnt.rojo} bg="#fee2e2" color="#dc2626" />
            <KpiCard icon="👥" label="Residentes" value={totalRes} bg="#f1f5f9" color="#1e293b" />
            <KpiCard icon="⚠️" label="Alertas resid." value={totalAlerts} bg="#fff7ed" color="#ea580c" />
          </div>

          <BarraBusqueda
            busqueda={busqueda} setBusqueda={setBusqueda}
            orden={orden} setOrden={setOrden}
            placeholder="🔍 Buscar piso…"
            ordenes={[
              { key: 'estado', label: 'Peor estado primero' },
              { key: 'nombre', label: 'Nombre A–Z' },
              { key: 'residentes', label: 'Más residentes' },
              { key: 'reportes', label: 'Más reportes' },
            ]}
          />

          <div style={{ padding: '0 14px' }}>
            <EstadoCarga vacio={vacio} />
            {pisos.map(p => <TarjetaPiso key={p.id_piso} p={p} delta={deltaDe(p)} onClick={() => setSel(p.id_piso)} />)}
          </div>
        </>
      )}

      {sel !== null && selObj && <DetalleModal p={selObj} delta={deltaDe(selObj)} onClose={() => setSel(null)} />}
    </PageWrap>
  );
}
